import PropTypes from 'prop-types';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CONNECTION_STATE_CHANGE, generateClient } from 'aws-amplify/data';
import { Hub } from 'aws-amplify/utils';
import { onCreateSensorAlerts } from '../graphql/subscriptions';
import { useGeneralContext } from './GeneralContext';
import { useGetAll } from '../hooks/useGetAll';
import { defaultTimeout } from './SensorReadingsContext';

const NotificationsContext = createContext();

const client = generateClient();

const statusOrder = {
  DANGER: 0,
  WARNING: 1,
  NORMAL: 2,
};

const Provider = ({ children }) => {
  const { user } = useGeneralContext();
  const {
    data: { notification: notificationList } = { notification: [] },
    isFetching,
    refetch,
  } = useGetAll('notification', null, !!user);
  const [connection, setConnection] = useState({
    status: 'Disconnected',
    updated: new Date(),
  });
  const [notifications, setNotifications] = useState([]);
  const connectionClient = useRef(null);

  useEffect(() => {
    if (!isFetching) setNotifications(notificationList ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching]);

  const subscribe = useCallback(() => {
    try {
      if (!user) return;
      if (connectionClient.current) connectionClient.current.unsubscribe();
      connectionClient.current = client
        .graphql({
          query: onCreateSensorAlerts,
          variables: { user_id: user.email },
        })
        .subscribe({
          next: ({ data: { onCreateSensorAlerts } }) => {
            setNotifications(prev => {
              const newNotifications = prev.concat(
                onCreateSensorAlerts.alerts.map(alert =>
                  JSON.parse(alert.parameters),
                ),
              );
              newNotifications.sort((a, b) => {
                const statusComparison =
                  statusOrder[a.status] - statusOrder[b.status];
                if (statusComparison !== 0) {
                  return statusComparison;
                }

                const dateA = new Date(a.created_at);
                const dateB = new Date(b.created_at);
                return dateB.getTime() - dateA.getTime();
              });
              return newNotifications;
            });
          },
          error: error => {
            console.warn(error);
            setConnection({ status: 'Error', updated: new Date() });
          },
        });
    } catch (error) {
      console.error(error);
      setConnection({ status: 'Error', updated: new Date() });
    }
  }, [user]);

  const unsubscribe = () => {
    connectionClient.current?.unsubscribe();
    setConnection({ status: 'Disconnected', updated: new Date() });
  };

  useEffect(() => {
    const hubListenerCancelToken = Hub.listen('api', data => {
      const { payload } = data;
      if (payload.event === CONNECTION_STATE_CHANGE) {
        const { connectionState } = payload.data;
        setConnection({ status: connectionState, updated: new Date() });
      }
    });

    const timeout = user?.['custom:timeout'] || defaultTimeout;
    let interval;
    if (timeout > 0) interval = setInterval(subscribe, timeout);
    subscribe();
    return () => {
      unsubscribe();
      hubListenerCancelToken();
      clearInterval(interval);
    };
  }, [subscribe, user]);

  const value = useMemo(
    () => ({
      connection,
      notifications,
      subscribe,
      unsubscribe,
      isFetching,
      refetch,
    }),
    [connection, notifications, subscribe, isFetching, refetch],
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotificationsContext = () => useContext(NotificationsContext);

Provider.propTypes = {
  children: PropTypes.element.isRequired,
};

export default Provider;
